import { LargeTitle } from "@/components/common/LargeTitle";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCategory } from "@/hooks/useMovies";
import { MovieGrid } from "@/components/movie/MovieRow";
import { GridSkeleton, Shimmer } from "@/components/skeleton/Skeletons";
import { EmptyState, ErrorState } from "@/components/common/States";
import { Page, Reveal } from "@/components/common/Reveal";
import { GlassButton, Segmented } from "@/components/glass/Glass";
import { SORT_OPTIONS } from "@/components/search/FilterSheet";
import { IconChevronLeft, IconGrid } from "@/components/icons/Icons";
import { useSeo } from "@/lib/seo";

export default function Category() {
  const { slug } = useParams();
  const [sort, setSort] = useState("popular");
  const { data, isLoading, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useCategory(slug, sort);
  const items = useMemo(() => (data?.pages || []).flatMap((p) => p.items), [data]);
  const genre = data?.pages?.[0]?.genre;
  const total = data?.pages?.[0]?.total || 0;
  useSeo({ title: genre ? `${genre.name} filmlar` : "Kategoriya", description: genre ? `${genre.name} janridagi eng yaxshi o‘zbek filmlari.` : undefined });

  if (isError && error?.status === 404) {
    return (
      <Page className="pk-pb-dock pt-32">
        <EmptyState icon={IconGrid} title="Kategoriya topilmadi" action={<Link to="/categories"><GlassButton>Barcha kategoriyalar</GlassButton></Link>} testId="category-not-found" />
      </Page>
    );
  }

  return (
    <Page className="pk-pb-dock pt-[calc(76px+var(--safe-top))] lg:pt-28">
      <div className="pk-container">
        <Link to="/categories" className="inline-flex items-center gap-1 text-[14px] font-semibold text-white/55 hover:text-white" data-testid="category-back-link">
          <IconChevronLeft size={17} /> Kategoriyalar
        </Link>
        <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          {genre ? (
            <Reveal>
              <LargeTitle className="text-[32px] font-extrabold tracking-[-0.025em] text-white sm:text-[42px]" testId="category-title">{genre.name}</LargeTitle>
              <p className="mt-1 text-[14px] font-medium text-white/50">{total} ta film</p>
            </Reveal>
          ) : (
            <div>
              <Shimmer className="h-10 w-48 rounded-[12px]" />
              <Shimmer className="mt-2 h-4 w-20 rounded-full" />
            </div>
          )}
          <Segmented options={SORT_OPTIONS} value={sort} onChange={setSort} testId="category-sort" size="sm" className="self-start sm:self-auto" />
        </div>
        <div className="mt-8">
          {isLoading ? (
            <GridSkeleton />
          ) : isError ? (
            <ErrorState error={error} onRetry={refetch} />
          ) : !items.length ? (
            <EmptyState icon={IconGrid} title="Bu kategoriyada hozircha film yo‘q" testId="category-empty" />
          ) : (
            <MovieGrid items={items} hasMore={Boolean(hasNextPage)} loadMore={fetchNextPage} loadingMore={isFetchingNextPage} testId="category-grid" />
          )}
        </div>
      </div>
    </Page>
  );
}
